<template>
  <div
    v-if="pegLoaded"
    class="settings-section"
    data-testid="config-ia-peg-form"
  >
    <h4 class="section-title">{{ t('Peg global de créditos (USD por crédito)') }}</h4>
    <p class="section-subtitle">
      {{ t('Vale para toda franquia que não tenha um peg próprio na tabela abaixo.') }}
    </p>
    <div class="form-fields">
      <input-text
        v-model="pegUsd"
        elem-id="config-ia-peg-input"
        :label="t('Peg (USD por crédito)')"
        data-testid="config-ia-peg-input"
      />
    </div>
    <p v-if="pegHint" class="peg-hint" data-testid="config-ia-peg-hint">
      {{ pegHint }}
    </p>
    <div v-if="errorMessage" class="error-message" data-testid="config-ia-peg-error">
      {{ errorMessage }}
    </div>
    <div class="form-actions">
      <Button
        :label="t('Salvar peg')"
        icon="pi pi-save"
        severity="primary"
        :loading="savingPeg"
        data-testid="config-ia-peg-save-button"
        @click="handleSavePeg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from '../../../ui/AppButton.vue'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useAiSettings } from '../../../composables/useAiSettings'
import { emitValidationFailed } from '../../../composables/useFormValidation'

/**
 * The GLOBAL peg — layer 2 of three (franchise override → global → coded
 * default). Lives on the provider-cost tab, beside the per-franchise table it
 * is the fallback for: the two answer one question and reading them apart
 * invited exactly the drift this whole change removed.
 *
 * Moved here from ConfiguracoesIaSettingsForm (2026-08-21), which also owns
 * max-context — an unrelated knob that happened to share a tab.
 *
 * STRING, not number. It was a numeric input capped at 8 decimal places;
 * both had to change. JavaScript renders any value below 1e-6 in exponential
 * notation (String(0.000000001) === '1e-9'), so a numeric binding silently
 * corrupts precisely the small pegs that make credits burn fast — the useful
 * end of the range.
 */
const { t } = useI18n()
const { peg, loadPeg, savePeg, savingPeg } = useAiSettings()

/** Matches AiCredits_PegOverrideInput::MAX_DECIMALS. */
const MAX_DECIMALS = 12

const pegLoaded = ref(false)
const pegUsd = ref<string>('')
const errorMessage = ref<string>('')

// A typical prompt's provider cost, for the business-terms hint below — see
// project memory "ai-credits-pricing-and-reserve-followups" (~$0.0004 COGS per
// prompt). The peg is designed so 1 credit ≈ 1 typical prompt.
const TYPICAL_PROMPT_COST_USD = 0.0004

// A bare "0.0004" is illegible; this says what it MEANS in business terms.
const pegHint = computed<string | null>(() => {
  const value = Number(pegUsd.value)
  if (!pegUsd.value || Number.isNaN(value) || value <= 0) return null
  const creditsPerPrompt = TYPICAL_PROMPT_COST_USD / value
  const promptsPer500 = 500 / creditsPerPrompt
  // Split rather than interpolated — see PegOverrideForm for why: an
  // untranslated key is returned verbatim and never interpolated, so a
  // placeholder inside the key shows up as literal text.
  const credits = creditsPerPrompt.toFixed(creditsPerPrompt < 1 ? 4 : 2)
  const prompts = Math.round(promptsPer500)
  return `${t('Um prompt típico de ~$0,0004 custa')} ~${credits} ${t('crédito(s)')}`
    + ` — ${t('um pacote de 500 créditos rende')} ~${prompts} ${t('prompts')}.`
})

function validate(): string {
  const raw = pegUsd.value.trim()
  if (raw === '') return t('Informe o peg')
  if (/e/i.test(raw)) {
    return t('Use notação decimal simples, não exponencial (0.000000001, não 1e-9)')
  }
  if (Number.isNaN(Number(raw))) return t('O peg deve ser um número decimal')
  if (Number(raw) <= 0) return t('O peg deve ser maior que zero')
  const dot = raw.indexOf('.')
  if (dot !== -1 && raw.length - dot - 1 > MAX_DECIMALS) {
    return `${t('O peg aceita no máximo')} ${MAX_DECIMALS} ${t('casas decimais')}`
  }
  return ''
}

async function handleSavePeg(): Promise<void> {
  const problem = validate()
  errorMessage.value = problem
  if (problem !== '') {
    emitValidationFailed('GlobalPegForm', { ai_credit_peg_usd: problem })
    eventBus.emit('globalMessage.error', problem)
    return
  }

  try {
    await savePeg({ ai_credit_peg_usd: pegUsd.value.trim() })
    eventBus.emit('globalMessage.success', t('Peg salvo com sucesso'))
    // The per-franchise form shows this as its placeholder — keep them in step.
    eventBus.emit('globalPeg.saved')
  } catch (err) {
    log('GlobalPegForm', 'handleSavePeg', 'Erro ao salvar peg', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar peg'))
  }
}

onMounted(async () => {
  try {
    await loadPeg()
    pegUsd.value = peg.value?.ai_credit_peg_usd ?? ''
  } catch (err) {
    log('GlobalPegForm', 'onMounted', 'Erro ao carregar peg', err)
  } finally {
    pegLoaded.value = true
  }
})
</script>

<style scoped>
.settings-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.section-title {
  margin: 0 0 4px;
}

.section-subtitle {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.peg-hint {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.error-message {
  color: var(--p-red-500, #ef4444);
  font-size: 0.9rem;
  margin-top: 8px;
}

.form-actions {
  margin-top: 12px;
}
</style>
